import { useState, useEffect } from "react";
import { X, Upload, Save } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

export interface NoteData {
  id: number;
  content: string;
  date: string;
  author: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: NoteData) => void;
  editData?: NoteData | null;
  loading?: boolean;
}

export default function NotesModal({ isOpen, onClose, onSave, editData, loading = false }: Props) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [date, setDate] = useState("Today");

  useEffect(() => {
    if (editData) {
      setContent(editData.content);
      setDate(editData.date);
    } else {
      setContent("");
      setDate("Today");
    }
  }, [editData, isOpen]);

  const handleSave = () => {
    if (!content.trim()) return;

    const noteData: NoteData = {
      id: editData?.id || Date.now(),
      content: content.trim(),
      date: date || new Date().toISOString().split('T')[0],
      author: editData?.author || user?.name || "Staff Member",
    };

    onSave(noteData);
    // onClose(); // Let parent handle closing on success
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            {editData ? "Edit a note" : "Add a note"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">

          <div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add a note here..."
              className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${loading
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-[#885ABB] text-white hover:bg-[#6f47a2]"
              }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="opacity-70"
            >
              <path
                d="M2 14V2h8l4 4v8H2z"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
